import React, { useState, useEffect } from 'react';
import { Home, Thermometer } from 'lucide-react';
import { Device } from '../../types';
import deviceService from '../../services/deviceService';
import {
  SensorPanelsContainer,
  LeftColumn,
  RightColumn,
  SensorPanel,
  PanelHeader,
  PanelIcon,
  PanelTitle,
  SensorList,
  SensorRow,
  SensorLabel,
  SensorValue
} from '../../styles/SensorPanelStyles';

interface NetatmoIndoorDetailsProps {
  device: Device;
  deviceId: string;
}

interface NetatmoData {
  temperature?: number;
  humidity?: number;
  co2?: number;
  battery_percent?: number;
  rf_status?: number;
  last_seen?: string;
}

const NetatmoIndoorDetails: React.FC<NetatmoIndoorDetailsProps> = ({ device, deviceId }) => {
  const [latestData, setLatestData] = useState<NetatmoData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestData = async () => {
      try {
        const data = await deviceService.getLatestData(deviceId);
        if (data && data.decoded_json) {
          setLatestData(data.decoded_json);
        }
      } catch (error) {
        console.error('Error fetching latest data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestData();
  }, [deviceId]);

  const formatTemperature = (temp: number | undefined): string => {
    return temp !== undefined ? `${temp.toFixed(1)}°C` : 'N/A';
  };

  const formatHumidity = (humidity: number | undefined): string => {
    return humidity !== undefined ? `${humidity.toFixed(0)}%` : 'N/A';
  };

  const formatCO2 = (co2: number | undefined): string => {
    return co2 !== undefined ? `${co2.toFixed(0)} ppm` : 'N/A';
  };

  const formatBattery = (battery: number | undefined): string => {
    return battery !== undefined ? `${battery.toFixed(0)}%` : 'N/A';
  };

  const getBatteryStatus = (battery: number | undefined): 'normal' | 'warning' | 'critical' => {
    if (battery === undefined) return 'normal';
    if (battery > 60) return 'normal';
    if (battery > 20) return 'warning';
    return 'critical';
  };

  const getSignalQuality = (rfStatus: number | undefined): string => {
    if (rfStatus === undefined) return 'Unknown';
    if (rfStatus > 80) return 'Excellent';
    if (rfStatus > 60) return 'Good';
    if (rfStatus > 40) return 'Fair';
    return 'Weak';
  };

  if (loading) {
    return (
      <SensorPanelsContainer>
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          Loading indoor module data...
        </div>
      </SensorPanelsContainer>
    );
  }

  return (
    <SensorPanelsContainer>
      <LeftColumn>
        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <Home size={16} />
            </PanelIcon>
            <PanelTitle>Indoor Environment</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>Temperature</SensorLabel>
              <SensorValue status="normal">{formatTemperature(latestData?.temperature)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Humidity</SensorLabel>
              <SensorValue status="normal">{formatHumidity(latestData?.humidity)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>CO₂ Level</SensorLabel>
              <SensorValue status={latestData?.co2 !== undefined ? 'normal' : 'inactive'}>{formatCO2(latestData?.co2)}</SensorValue>
            </SensorRow>
          </SensorList>
        </SensorPanel>
      </LeftColumn>

      <RightColumn>
        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <Thermometer size={16} />
            </PanelIcon>
            <PanelTitle>Device Health</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>Battery Level</SensorLabel>
              <SensorValue status={getBatteryStatus(latestData?.battery_percent)}>{formatBattery(latestData?.battery_percent)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Signal Quality</SensorLabel>
              <SensorValue status="normal">{getSignalQuality(latestData?.rf_status)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Last Seen</SensorLabel>
              <SensorValue status="normal">
                {latestData?.last_seen ? new Date(latestData.last_seen).toLocaleString() : 'N/A'}
              </SensorValue>
            </SensorRow>
          </SensorList>
        </SensorPanel>
      </RightColumn>
    </SensorPanelsContainer>
  );
};

export default NetatmoIndoorDetails;
